import { ICdwTableColumnData } from './types';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import {
  dataModelerSelectCurrentDraftEdits,
  dataModelerSelectCurrentSchemaObj,
} from '../../../../redux/selectors/dataModeler';
import { defaultEmptyDraftEdits } from '../../../../redux/slices/dataModeler/dataModelerSlice';
import { assertNotNil } from '../../../../utils/types';
import {
  dataModelerAddDraftFieldData,
  dataModelerExtendFactSubject,
  dataModelerSetEventColumn,
  dataModelerSetEventTemporalColumn,
  dataModelerSetInstallTemporalColumn,
  dataModelSetDraftFactTableAutoMapped,
  IDataModelerDraftEdits,
} from '../../../../redux/slices/dataModeler';
import {
  FieldClass,
  ISchemaDataModelFieldsFragment,
} from '../../../../utils/apollo/graphqlSchema';
import { AppDispatch } from '../../../../redux/store';
import { getModelField, temporalFieldKeysConfig } from '../utils';
import {
  findExtendingFieldKeyAndClass,
  isSameColumnAndTypeInputDefinition,
  isSameColumnAndTypeParamsDefinition,
} from '../../../../redux/slices/dataModeler/utils';

const autoMapTableColumn = (
  tableKey: string,
  columnData: ICdwTableColumnData,
  schemaObj: ISchemaDataModelFieldsFragment,
  draftEdits: IDataModelerDraftEdits,
  dispatch: AppDispatch,
) => {
  // try to map the event field
  let mappedEvent = false;
  const existingEventField = schemaObj.fact.fields.find(
    (fieldMeta) => fieldMeta.fieldClass === FieldClass.Event,
  );
  if (existingEventField) {
    if (
      existingEventField.tableFields.some((tableField) =>
        isSameColumnAndTypeParamsDefinition(tableField, columnData),
      )
    ) {
      dispatch(
        dataModelerSetEventColumn({
          tableColumn: columnData.tableColumn,
          tableKey,
        }),
      );
      mappedEvent = true;
    }
  } else {
    const newEventField = draftEdits.draftFieldsData.find(
      (draftField) =>
        draftField.addInput?.typeInput?.fieldClass === FieldClass.Event,
    );
    if (
      newEventField &&
      isSameColumnAndTypeInputDefinition(newEventField.addInput, columnData)
    ) {
      dispatch(
        dataModelerSetEventColumn({
          tableColumn: columnData.tableColumn,
          tableKey,
        }),
      );
      mappedEvent = true;
    }
  }
  // try to map the subject fields - existing first
  let mappedSubject = false;
  schemaObj.subjects.forEach((subj) => {
    if (subj.viewKey === schemaObj.fact.key) {
      const factField = getModelField(schemaObj, subj.fieldKey);
      if (
        factField?.tableFields?.some((tableField) =>
          isSameColumnAndTypeParamsDefinition(tableField, columnData),
        )
      ) {
        dispatch(
          dataModelerExtendFactSubject({
            subjectKey: subj.subjectKey,
            tableKey,
            tableColumn: columnData.tableColumn,
          }),
        );
        mappedSubject = true;
      }
    }
  });
  // newly added subjects
  draftEdits.addedSubjects.forEach((draftSubj) => {
    if (draftSubj.viewKey === schemaObj.fact.key) {
      const draftField = draftEdits.draftFieldsData.find(
        (draftField) => draftField.addInput?.key === draftSubj.input.fieldKey,
      );
      if (
        draftField &&
        isSameColumnAndTypeInputDefinition(draftField.addInput, columnData)
      ) {
        dispatch(
          dataModelerExtendFactSubject({
            subjectKey: draftSubj.subjectKey,
            tableKey,
            tableColumn: columnData.tableColumn,
          }),
        );
        mappedSubject = true;
      }
    }
  });
  // try to map the event date/time/etc
  const eventTemporals = schemaObj.eventTemporals;
  let mappedTemporal = false;
  for (const { temporalKey, requiredType } of temporalFieldKeysConfig) {
    if (
      eventTemporals[temporalKey]?.columnRefs.some(
        (colRef) =>
          colRef.column === columnData.column &&
          !colRef.isAutoGenerated &&
          columnData.type === requiredType,
      )
    ) {
      mappedTemporal = true;
      dispatch(
        dataModelerSetEventTemporalColumn({
          tableColumn: columnData.tableColumn,
          tableKey,
          temporalField: temporalKey,
        }),
      );
    }
  }
  // try to map any install date/time/etc
  const installTemporals = schemaObj.installTemporals;
  let mappedInstallTemporal = false;
  if (installTemporals && installTemporals.viewKey === schemaObj.fact.key) {
    for (const { temporalKey, requiredType } of temporalFieldKeysConfig) {
      if (
        installTemporals[temporalKey]?.columnRefs.some(
          (colRef) =>
            colRef.column === columnData.column &&
            !colRef.isAutoGenerated &&
            columnData.type === requiredType,
        )
      ) {
        mappedInstallTemporal = true;
        dispatch(
          dataModelerSetInstallTemporalColumn({
            viewKey: schemaObj.fact.key,
            tableColumn: columnData.tableColumn,
            tableKey,
            temporalField: temporalKey,
          }),
        );
      }
    }
  }
  // try to map remaining basic columns
  if (
    !mappedTemporal &&
    !mappedInstallTemporal &&
    !mappedEvent &&
    !mappedSubject
  ) {
    const { fieldKey: extendingFieldKey } = findExtendingFieldKeyAndClass(
      schemaObj.fact.key,
      tableKey,
      columnData.tableColumn,
      schemaObj.fact.fields,
      draftEdits.draftFieldsData,
    );
    if (extendingFieldKey) {
      dispatch(
        dataModelerAddDraftFieldData({
          viewKey: schemaObj.fact.key,
          tableKey,
          column: columnData.tableColumn,
        }),
      );
    }
  }
};

const useFactTableAutoMapFields = ({
  tableKey,
  columns,
}: {
  tableKey: string;
  columns: ICdwTableColumnData[];
}): {
  needAutoMap: boolean;
  autoMapColumns?: () => void;
} => {
  const dispatch = useAppDispatch();
  const draftEdits =
    useAppSelector(dataModelerSelectCurrentDraftEdits) ??
    defaultEmptyDraftEdits;
  const schemaObj = useAppSelector(dataModelerSelectCurrentSchemaObj);
  assertNotNil(schemaObj);
  const draftFactTable = draftEdits.addedFactsData.find(
    (factData) => factData.tableKey === tableKey,
  );
  const needAutoMap = draftFactTable ? !draftFactTable.autoMapped : false;
  if (!needAutoMap || !draftFactTable) {
    return { needAutoMap: false };
  }
  const autoMapColumns = () => {
    // TODO this is not exactly right, need a flag whether the columns were loaded
    if (columns.length === 0) {
      return;
    }
    columns.forEach((columnData) => {
      autoMapTableColumn(tableKey, columnData, schemaObj, draftEdits, dispatch);
    });
    dispatch(dataModelSetDraftFactTableAutoMapped({ tableKey }));
  };
  return {
    needAutoMap,
    autoMapColumns,
  };
};

export default useFactTableAutoMapFields;
